import { Skeleton, Text, useDisclosure } from "@chakra-ui/react";
import * as React from "react";

import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  Row,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";

// Custom components
import Card from "components/card/Card";
import ListTable, { LIST_MUTED_COLOR, ListHeader, ListRow } from "components/list/ListTable";
import { usePagination } from "components/list/TablePagination";
import { useForfait } from "hooks/useForfait";
import { useForfaitUsage } from "hooks/useForfaitUsage";
import { useListState } from "hooks/useListState";
import ModalForm from "components/modal/modalForm";
import ModalDelete from "components/modal/modalDelete";
import MainContact from "components/user/MainContact";
import ForfaitRemaining, { LastIntervention } from "components/user/ForfaitRemaining";
import ListMenuClient from "components/menu/ListMenuClient";

type TableClientType = {
  id: string;
  raison_sociale: string;
  adresse: string;
  cp: string;
  ville: string;
};

const columnHelper = createColumnHelper<TableClientType>();

// Forfait actif d'un client : undefined pendant la lecture, null s'il n'en a pas
function useActiveForfait(idClient: string) {
  const [forfait, setForfait] = React.useState<ForfaitType | null>();
  const { getForfaitByClient } = useForfait();
  React.useEffect(() => {
    let isMounted = true; // Variable de suivi pour vérifier si le composant est monté
    const fetchData = async () => {
      const fetchedForfaits = await getForfaitByClient(idClient);
      if (isMounted) {
        setForfait(fetchedForfaits?.length ? fetchedForfaits[0] : null);
      }
    };
    fetchData();
    return () => {
      isMounted = false; // Définir la variable isMounted sur false lors du démontage du composant
    };
  }, [idClient, getForfaitByClient]);
  return forfait;
}

// Ligne du tableau : le forfait actif n'est lu qu'une fois, pour les deux colonnes qui l'affichent.
// Un client sans forfait actif est atténué, ce n'est pas une alerte
function ClientRow(props: { row: Row<TableClientType> }) {
  const { row } = props;
  const forfait = useActiveForfait(row.original.id);
  const usage = useForfaitUsage(forfait?.id);
  return <ListRow row={row} muted={forfait === null} cellContext={{ forfait, usage }} />;
}

// const columns = columnsDataCheck;
export default function ColumnTable(props: { tableData: any }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const modalDelete = useDisclosure();
  const { tableData } = props;
  const [sorting, setSorting] = useListState<SortingState>("clients:tri", [
    { id: "raison_sociale", desc: false },
  ]);
  const [selectedClient, setSelectedClient] = React.useState<
    ClientType[] | null
  >(null);
  let defaultData = tableData;
  // Colonnes créées une seule fois : de nouvelles fonctions `cell` à chaque rendu
  // feraient remonter toutes les cellules (et relancer leurs lectures Firestore)
  const columns = React.useMemo(
    () => [
      columnHelper.accessor("raison_sociale", {
        id: "raison_sociale",
        header: "Raison sociale",
        cell: (info) => (
          <>
            <Text fontWeight="700">{info.getValue()}</Text>
            <Text color={LIST_MUTED_COLOR} fontSize="xs" lineHeight="16px">
              <MainContact id={info.row.original.id} />
            </Text>
          </>
        ),
      }),
      columnHelper.display({
        id: "forfait_actif",
        header: "Forfait actif",
        meta: { width: { base: "220px", "2xl": "28%" } },
        cell: (info: any) => {
          if (info.forfait === undefined) {
            return <Skeleton h="8px" w="100%" borderRadius="full" />;
          }
          return info.forfait ? (
            <ForfaitRemaining forfait={info.forfait} usage={info.usage} />
          ) : (
            <>Aucun forfait</>
          );
        },
      }),
      columnHelper.display({
        id: "derniere_intervention",
        header: "Dernière interv.",
        meta: { width: { base: "140px", "2xl": "16%" } },
        cell: (info: any) =>
          info.forfait === null ? <>—</> : <LastIntervention usage={info.usage} />,
      }),
      columnHelper.accessor("ville", {
        id: "ville",
        header: "Ville",
        meta: { width: { base: "250px", "2xl": "30%" } },
        cell: (info) => (
          <>
            <Text>
              {info.getValue()}
              {info.row.original.cp && ` (${String(info.row.original.cp).trim()})`}
            </Text>
            <Text
              color={LIST_MUTED_COLOR}
              fontSize="xs"
              lineHeight="16px"
              noOfLines={2}
              title={info.row.original.adresse}
            >
              {info.row.original.adresse}
            </Text>
          </>
        ),
      }),
      columnHelper.display({
        id: "actions",
        meta: { width: "67px", align: "right" },
        cell: (info) => (
          <ListMenuClient
            clientrow={[info.row.original]}
            openmodal={onOpen}
            setselectedclient={setSelectedClient}
            opendeletemodal={modalDelete.onOpen}
          />
        ),
      }),
    ],
    [onOpen, modalDelete.onOpen]
  );
  const [data, setData] = React.useState(() => [...defaultData]);
  const [pagination, setPagination] = usePagination(data.length);
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      pagination,
    },
    onSortingChange: (updater) => {
      setSorting(updater);
      table.setPageIndex(0);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // Lignes identifiées par leur document : après un tri ou un changement de page,
    // chaque cellule garde son enregistrement (et ne relance pas ses lectures Firestore)
    getRowId: (row) => row.id,
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    // Garde la page après une modification ; le tri, les filtres et les ajouts reviennent à la première
    autoResetPageIndex: false,
  });
  // Un ajout revient à la première page, où le tri par défaut place la nouvelle ligne
  function handleFormData(newData: any[]) {
    setData(newData);
    if (!selectedClient) {
      table.setPageIndex(0);
    }
  }
  function handleAdd() {
    setSelectedClient(null);
    onOpen();
  }
  return (
    <Card flexDirection="column" w="100%" px="0px" pt="16px" pb="8px">
      <ListHeader
        title={`Liste des clients (${data.length})`}
        addLabel="Ajouter un client"
        onAdd={handleAdd}
      />
      <ListTable
        table={table}
        renderRow={(row) => <ClientRow key={row.id} row={row} />}
      />
      <ModalForm<ClientType>
        isOpen={isOpen}
        onClose={onClose}
        newData={handleFormData}
        updateData={selectedClient}
        form="client"
      />
      {selectedClient && (
        <ModalDelete
          isOpen={modalDelete.isOpen}
          onClose={modalDelete.onClose}
          idDelete={selectedClient[0].id}
          field={selectedClient[0].raison_sociale}
          type={"client"}
          newdata={setData}
        />
      )}
    </Card>
  );
}
